import React from 'react';
import PropTypes from 'prop-types';
import { LuTriangleAlert } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import useTariffText from '../tariffText';

export const initialsOf = (name) => {
  const words = String(name || '').split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '?';
  return `${words[0][0] || ''}${words.length > 1 ? words[1][0] || '' : ''}`.toLocaleUpperCase('tr-TR');
};

const isBlank = (value) => value === undefined || value === null || value === '' || (typeof value === 'number' && Number.isNaN(value));

/** FTE (teal) / Saat (violet) pill. */
export const TypeBadge = ({ type }) => {
  const { typeLabel } = useTariffText();
  if (!type) return <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;
  return (
    <span className={`tw-inline-flex tw-rounded-lg tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${type === 'FTE' ? 'tw-bg-teal-500/10 tw-text-teal-700 dark:tw-text-teal-300' : 'tw-bg-violet-500/10 tw-text-violet-700 dark:tw-text-violet-300'}`}>
      {typeLabel(type)}
    </span>
  );
};

TypeBadge.propTypes = { type: PropTypes.string };

/** Aktif / Pasif (FTE values taken off a tariff are kept as passive). */
export const ActiveBadge = ({ active }) => {
  const { label } = useTariffText();
  const passive = active === false;
  return (
    <Badge tone={passive ? 'neutral' : 'success'}>
      <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${passive ? 'tw-bg-slate-400' : 'tw-bg-emerald-500'}`} />
      {passive ? label('status.passive') : label('status.active')}
    </Badge>
  );
};

ActiveBadge.propTypes = { active: PropTypes.bool };

/** Evet / Hayır as a pill in the current language. */
export const YesNoBadge = ({ value }) => {
  const { yesNo } = useTariffText();
  if (!value) return <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;
  return <Badge tone={value === 'Evet' ? 'info' : 'neutral'}>{yesNo(value)}</Badge>;
};

YesNoBadge.propTypes = { value: PropTypes.string };

/** Project name; a tariff whose project was deleted says so instead of an id. */
export const ProjectName = ({ tariff }) => {
  const { label } = useTariffText();
  if (tariff.projectMissing) {
    return (
      <span title={label('table.deletedProjectHint')} className="tw-inline-flex tw-items-center tw-gap-1 tw-italic tw-text-amber-700 dark:tw-text-amber-300">
        <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
        {label('table.deletedProject')}
      </span>
    );
  }
  return <span className="tw-block tw-truncate">{isBlank(tariff.projectName) ? '-' : String(tariff.projectName)}</span>;
};

ProjectName.propTypes = { tariff: PropTypes.object.isRequired };

/** Plain-text form of a cell, for its tooltip. */
export const getCellTitle = (tariff, key) => {
  if (key === 'projectName' && tariff.projectMissing) return undefined;
  if (['active', 'isSubcontractor', 'type', 'currency'].includes(key)) return undefined;
  const value = tariff[key];
  return isBlank(value) || typeof value === 'object' ? undefined : String(value);
};

/** Content of one tariff table cell. */
const TariffCell = ({ columnKey, tariff }) => {
  const { formatDate, formatMoney } = useTariffText();
  const value = tariff[columnKey];
  const muted = <span className="tw-text-slate-400 dark:tw-text-slate-500">-</span>;

  switch (columnKey) {
    case 'customerName':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400/90 tw-to-violet-600/90 tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
            {initialsOf(value)}
          </span>
          <span className="tw-min-w-0 tw-truncate tw-font-semibold tw-text-slate-900 dark:tw-text-white">{isBlank(value) ? '-' : String(value)}</span>
        </span>
      );
    case 'projectName':
      return <ProjectName tariff={tariff} />;
    case 'type':
      return <TypeBadge type={value} />;
    case 'amount':
      return <span className="tw-block tw-truncate tw-font-semibold tw-tabular-nums tw-text-slate-900 dark:tw-text-white">{formatMoney(tariff.amountValue, tariff.currency)}</span>;
    case 'currency':
      return isBlank(value) ? muted : <span className="tw-rounded-md tw-bg-slate-900/[0.05] tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-slate-600 dark:tw-bg-white/[0.07] dark:tw-text-slate-300">{String(value)}</span>;
    case 'date':
      return isBlank(value) ? muted : <span className="tw-block tw-truncate tw-tabular-nums">{formatDate(value)}</span>;
    case 'isSubcontractor':
      return <YesNoBadge value={isBlank(value) ? '' : String(value)} />;
    case 'active':
      return <ActiveBadge active={tariff.active} />;
    case 'value':
    case 'price':
      return isBlank(value) ? muted : <span className="tw-block tw-truncate tw-tabular-nums">{String(value)}</span>;
    default:
      return isBlank(value) ? muted : <span className="tw-block tw-truncate">{String(value)}</span>;
  }
};

TariffCell.propTypes = { columnKey: PropTypes.string.isRequired, tariff: PropTypes.object.isRequired };

/** One row of the card list (narrow screens). */
export const TariffCardRow = ({ tariff }) => {
  const { formatMoney } = useTariffText();
  return (
    <>
      <span aria-hidden="true" className="tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-indigo-400 tw-to-violet-600 tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
        {initialsOf(tariff.customerName)}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className="tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{tariff.customerName || '-'}</span>
          <ActiveBadge active={tariff.active} />
        </span>
        <span className="tw-mt-0.5 tw-block tw-truncate tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400"><ProjectName tariff={tariff} /></span>
        <span className="tw-mt-1.5 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
          <TypeBadge type={tariff.type} />
          <span className="tw-tabular-nums">{tariff.value}</span>
          <span aria-hidden="true">·</span>
          <span className="tw-font-semibold tw-tabular-nums tw-text-slate-800 dark:tw-text-slate-100">{formatMoney(tariff.amountValue, tariff.currency)}</span>
        </span>
      </span>
    </>
  );
};

TariffCardRow.propTypes = { tariff: PropTypes.object.isRequired };

export default TariffCell;
